import { Info } from 'lucide-react';
import type { AssistantRiskData, Redactable } from '@cumpli/shared';
import { Pill } from '../../ui';
import { CHART_HEX } from '../../../lib/tokens';
import { useCountUp } from '../../../lib/motion';
import { CardShell } from './shell';
import { MaybeRedacted, isRedacted } from './redacted';
import { riesgoHex, riesgoLabel, riesgoToneOf } from './labels';

/** Parse "…22 pts" evidence values into a bar width + band color; null when there's no pts figure. */
function parseFactorBar(value: string): { pct: number; color: string } | null {
  const m = /(\d+)\s*pts?/.exec(value);
  if (!m || !m[1]) return null;
  const pts = Math.min(100, Math.max(0, Number(m[1])));
  const band = /\balto\b/i.test(value)
    ? 'alto'
    : /\bmedio\b/i.test(value)
    ? 'medio'
    : /\bbajo\b/i.test(value)
    ? 'bajo'
    : null;
  return { pct: pts, color: band ? riesgoHex(band) : riesgoHex('') };
}

/** One weighted-factor row: label + mono value, with a bar when the value carries a pts figure. */
function FactorRow({
  label,
  value,
}: {
  label: Redactable<string>;
  value: Redactable<string>;
}) {
  const bar = isRedacted(value) ? null : parseFactorBar(value);
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3">
        <span className="min-w-0 flex-1 truncate text-[12px] text-ink-soft">
          <MaybeRedacted value={label} />
        </span>
        <span className="shrink-0 font-mono text-[11px] text-ink">
          <MaybeRedacted value={value} />
        </span>
      </div>
      {bar && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-line">
          <div
            className="h-full rounded-full"
            style={{ width: `${bar.pct}%`, background: bar.color }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * `explain_client_risk` — a compact conic-gradient ring (score) beside the
 * client name, its risk-level pill, and the weighted factors that drove it.
 * The score may be redacted (ring shows the track only + an em dash). A missing
 * risk run degrades to an amber hint instead of an empty factor list.
 */
export function RiskCard({
  data,
  animate = false,
}: {
  data: AssistantRiskData;
  animate?: boolean | undefined;
}) {
  const scoreNum = isRedacted(data.score) ? null : data.score;
  const ringColor = isRedacted(data.riesgo) ? riesgoHex('') : riesgoHex(data.riesgo);
  // On a fresh answer the ring sweeps 0→score and the number counts up in lockstep.
  const sweeping = animate && scoreNum !== null;
  const swept = useCountUp(scoreNum ?? 0, { enabled: sweeping });
  const ringScore = sweeping ? swept : (scoreNum ?? 0);
  const deg = scoreNum === null ? 0 : Math.max(ringScore, 4) * 3.6;
  const noRun = data.hasRiskRun === false;
  const pep = data.pep === true;

  return (
    <CardShell>
      <div className="flex items-center gap-3">
        <div
          className="grid h-[60px] w-[60px] shrink-0 place-items-center rounded-full"
          style={{ background: `conic-gradient(${ringColor} ${deg}deg, ${CHART_HEX.gaugeTrack} 0)` }}
        >
          <div className="grid h-[46px] w-[46px] place-items-center rounded-full bg-panel">
            <span
              className="font-serif text-lg font-semibold leading-none"
              style={scoreNum === null ? undefined : { color: ringColor }}
            >
              {sweeping ? Math.round(swept) : <MaybeRedacted value={data.score} />}
            </span>
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate font-serif text-sm font-medium text-ink">
            <MaybeRedacted value={data.nombre} />
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Pill tone={isRedacted(data.riesgo) ? 'slate' : riesgoToneOf(data.riesgo)}>
              <MaybeRedacted value={data.riesgo} render={(r) => `Riesgo ${riesgoLabel(r)}`} />
            </Pill>
            {pep && <Pill tone="red">PEP</Pill>}
          </div>
        </div>
      </div>

      <div className="mt-3">
        {noRun ? (
          <div className="flex items-center gap-2 rounded-lg border border-tone-amber/40 bg-tone-amber-bg px-3 py-2 text-[12px] text-tone-amber">
            <Info className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            <span>Sin corrida de riesgo registrada.</span>
          </div>
        ) : data.factors.length > 0 ? (
          <div className="space-y-2">
            {data.factors.map((f, i) => (
              <FactorRow key={i} label={f.label} value={f.value} />
            ))}
          </div>
        ) : null}
      </div>

      <p className="mt-3 font-mono text-[10px] leading-relaxed text-ink-faint">
        El nivel lo asigna tu analista — Cumpli lo fundamenta según tu matriz.
      </p>
    </CardShell>
  );
}
